import type { Ref } from 'vue';

const FOCUS_ATTEMPT_LIMIT_MS = 1200;

interface FocusableSearchBox {
  focus: () => void;
}

/**
 * Focus a search field after client-side navigation.
 * The page enter transition and the previously focused link swallow the
 * first focus() call. A full reload has neither, so focus sticks immediately.
 */
export function useFocusSearchField(
  searchBoxRef: Ref<FocusableSearchBox | null>,
  rootRef: Ref<HTMLElement | null>
) {
  let requestId = 0;

  onBeforeUnmount(() => {
    requestId += 1;
  });

  function focusSearchField() {
    if (!import.meta.client) {
      return;
    }

    const currentRequest = ++requestId;
    const startedAt = performance.now();

    const attempt = () => {
      if (currentRequest !== requestId) {
        return;
      }

      const input = rootRef.value?.querySelector('input');
      const pageIsEntering = Boolean(
        input?.closest('.page-shift-enter-active')
      );

      if (!input || pageIsEntering) {
        if (performance.now() - startedAt < FOCUS_ATTEMPT_LIMIT_MS) {
          requestAnimationFrame(attempt);
        }
        return;
      }

      searchBoxRef.value?.focus();
      if (document.activeElement === input) {
        return;
      }

      if (performance.now() - startedAt < FOCUS_ATTEMPT_LIMIT_MS) {
        requestAnimationFrame(attempt);
      }
    };

    attempt();
  }

  return { focusSearchField };
}
